import React, { forwardRef, useRef, useImperativeHandle, useEffect } from "react";
import { NANO_PLAYER_URL } from "../../constants/index";
const NanoPlayer = forwardRef(({ stream, altStream = false }, ref) => {
  const playerRef = useRef(null);
  const qualities = useRef([]);
  const videoQuality = useRef(null);
  const paused = useRef(false);
  const muted = useRef(true);

  useEffect(() => {
    initNano();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream]);

  const initNano = () => {
    const self = {};
    self.player = document.createElement("iframe");
    const container = document.getElementById("video_container");
    if (container) {
      container.innerHTML = "";
      container.appendChild(self.player);
    }
    self.player.width = "100%";
    self.player.height = "100%";
    self.player.style.border = "none";
    self.player.src = NANO_PLAYER_URL;

    const streamConfig = altStream ? stream.config.alt : stream.config.qualities;
    qualities.current = Object.keys(streamConfig);

    const config = {
      source: {
        entries: qualities.current.map((q, i) => {
          const sCfg = streamConfig[q];
          return {
            index: i,
            label: q,
            info: {
              bitrate: sCfg.bitrate,
              width: sCfg.width,
              height: sCfg.height,
              framerate: sCfg.framerate
            },
            hls: '',
            h5live: {
              rtmp: {
                url: 'rtmp://bintu-play.nanocosmos.de/play',
                streamname: sCfg.streamId
              },
              server: {
                websocket: 'wss://bintu-play.nanocosmos.de:443/h5live/stream.mp4',
                hls: 'https://bintu-play.nanocosmos.de:443/h5live/http/playlist.m3u8',
                progressive: 'https://bintu-play.nanocosmos.de:443/h5live/http/stream.mp4'
              }
            },
            bintu: {}
          };
        }),
        options: {
          adaption: {
            rule: 'deviationOfMean'
          },
          switch: {}
        },
        startIndex: 0
      },
      playback: {
        autoplay: true,
        automute: true,
        muted: true,
        flashplayer: '//demo.nanocosmos.de/nanoplayer/nano.player.swf'
      },
      events: {},
      style: {
        displayMutedAutoplay: false,
        width: 'auto',
        height: 'auto',
        controls: false,
        scaling: 'fill',
        backgroundColor: 'transparent',
        buttonAnimation: false,
        poster: ''
      }
    };

    const iWindow = self.player.contentWindow;
    self.player.onload = function () {
      iWindow.postMessage({ type: "config", msg: config }, "*");
    };

    const listener = (event) => {
      if (event.data.type === "onReady") {
        safeCall(self, "play");
        if (videoQuality.current) setVideoQuality(videoQuality.current);
      } else if (event.data.type === "onPlay") {
        paused.current = false;
      } else if (event.data.type === "onPause") {
        paused.current = true;
      } else if (event.data.type === "onMute") {
        muted.current = true;
      } else if (event.data.type === "onUnmute") {
        muted.current = false;
      }
    };

    window.addEventListener("message", listener, false);

    playerRef.current = self;
  };

  const safeCall = (playerObj, methodName, ...args) => {
    if (playerObj && playerObj.player && typeof playerObj.player[methodName] === "function") {
      playerObj.player[methodName](...args);
    }
  };

  const setVideoQuality = (q) => {
    videoQuality.current = q;
    if (playerRef.current && playerRef.current.player) {
      playerRef.current.player.contentWindow.postMessage({ type: "changeQuality", msg: q }, "*");
    }
  };

  useImperativeHandle(ref, () => ({
    play: () => safeCall(playerRef.current, "play"),
    pause: () => safeCall(playerRef.current, "pause"),
    stop: () => safeCall(playerRef.current, "stop"),
    changeQuality: (q) => setVideoQuality(q),
    mute: () => safeCall(playerRef.current, "mute"),
    unmute: () => safeCall(playerRef.current, "unmute"),
    volume: (val) => safeCall(playerRef.current, "volume", val),
    destroy: () => safeCall(playerRef.current, "stop")
  }));

  return <div id="video_container" style={{ width: "100%", height: "100%" }} />;
});

export default NanoPlayer;